import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

// ---- types ----

export interface BacktestDay {
  date: string;
  official_midday_mw: number;
  actual_midday_mw: number;
  realization_ratio: number | null;
  realization_grade: string;
  shortfall_mw: number;
  is_high_pv: boolean;
  bucket_key: string | null;
  radiation_realization_ratio: number | null;
  cause_label: string;
  weather_adjusted_midday_mw: number | null;
  weather_preemptive_discount_mw: number | null;
}

export interface BacktestSummary {
  valid_days: number;
  high_pv_days: number;
  avg_realization_ratio: number | null;
  full_realization_rate: number | null;
  weak_or_failed_rate: number | null;
  failed_rate: number | null;
  avg_shortfall_mw: number;
  p80_shortfall_mw: number;
  max_shortfall_mw: number;
  weather_adjusted_capture: number | null;
}

export interface BacktestBucket {
  bucket_key: string;
  sample_days: number;
  p50_realization_ratio: number | null;
  p20_realization_ratio: number | null;
  weak_or_failed_rate: number | null;
  recommended_discount_ratio: number | null;
  risk_discount_ratio: number | null;
  status: string;
  applied: boolean;
}

export interface BacktestDataQuality {
  incomplete_days: number;
  himawari_missing_days?: number;
}

export interface HighPvRealizationBacktestResponse {
  summary: BacktestSummary;
  buckets: BacktestBucket[];
  days: BacktestDay[];
  data_quality: BacktestDataQuality;
}

// ---- hooks ----

export function useHighPvRealizationBacktest(
  startDate: string,
  endDate: string,
  modelName = 'ecmwf_ifs',
  minOfficialCapacityFactor = 0.50,
) {
  return useQuery({
    queryKey: ['weather', 'high-pv-realization-backtest', startDate, endDate, modelName, minOfficialCapacityFactor],
    queryFn: async () => {
      const res = await apiClient.get<HighPvRealizationBacktestResponse>(
        '/weather/high-pv-realization-backtest',
        { params: { start_date: startDate, end_date: endDate, model_name: modelName, min_official_capacity_factor: minOfficialCapacityFactor } },
      );
      return res.data;
    },
    staleTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
